import React, { useState, useEffect } from 'react';
import { Plus, Trash, X, Upload, Image as ImageIcon, Globe, Languages, Edit, GripVertical, Save, Search, PackageX, Share2 } from 'lucide-react';
import CountrySelector from '../../Common/CountrySelector/CountrySelector';
import './BannerProducts.css';
import { categoryAPI } from '../../../config/category-api';
import { filterCategoriesByAccess } from '../../../utils/categoryAccessFilter';
import { productAPI } from '../../../config/product-api';
import { bannerAPI } from '../../../config/banner-api';
import { authAPI } from '../../../config/auth-api';
import { toast } from 'react-toastify';
import LanguageSelector from '../../Common/LanguageSelector/LanguageSelector';
import { getCountryName, getLanguageName } from '../../../constants/constants';
import { ADMIN_PERMISSIONS } from '../../../constants/permissions';
import { helpers } from '../../../utils/helpers';
import { IS_ALFAJR } from '../../../constants/general';
import ImageCropperModal from '../../Common/ImageCropperModal/ImageCropperModal';

const BannerProducts = ({ adminData }) => {
  const canCreate = helpers.hasPermission(adminData, ADMIN_PERMISSIONS.CREATE_BANNER_PRODUCTS);
  const canEdit = helpers.hasPermission(adminData, ADMIN_PERMISSIONS.EDIT_BANNER_PRODUCTS);
  const canDelete = helpers.hasPermission(adminData, ADMIN_PERMISSIONS.DELETE_BANNER_PRODUCTS);

  const [showDialog, setShowDialog] = useState(false);
  const [showCountryDialog, setShowCountryDialog] = useState(false);
  const [isCategoryDialogOpen, setIsCategoryDialogOpen] = useState(false);
  const [isProductDialogOpen, setIsProductDialogOpen] = useState(false);
  const [isProductViewDialogOpen, setIsProductViewDialogOpen] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);

  const [banners, setBanners] = useState([]);
  const [editingBanner, setEditingBanner] = useState(null);
  const [bannerToDelete, setBannerToDelete] = useState(null);

  const [isReorderMode, setIsReorderMode] = useState(false);
  const [draggedBanner, setDraggedBanner] = useState(null);
  const [dragOverBanner, setDragOverBanner] = useState(null);

  const [draggedProduct, setDraggedProduct] = useState(null);
  const [dragOverProduct, setDragOverProduct] = useState(null);

  const [loadingBanners, setLoadingBanners] = useState(false);
  const [savingBanner, setSavingBanner] = useState(false);
  const [activeFilter, setActiveFilter] = useState('all');
  const [loadingCategories, setLoadingCategories] = useState(false);
  const [loadingProducts, setLoadingProducts] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [showLanguageDialog, setShowLanguageDialog] = useState(false);
  const [counts, setCounts] = useState({ all: 0, deals_promotions: 0, banner_slider: 0, top_product: 0, category_screen_banners: 0, popup_banner: 0 });
  const [showCropper, setShowCropper] = useState(false);
  const [cropperSrc, setCropperSrc] = useState(null);

  const [formData, setFormData] = useState({
    title: '',
    arabicTitle: '',
    type: 'deals_promotions',
    image: null,
    imagePreview: null,
    icon: null,
    iconPreview: null,
    description: '',
    color: '',
    applicationType: 'selected_categories',
    category: '',
    subCategory: '',
    products: [],
    targetType: 'all',
    country: '',
    language: '',
    hasButton: false,
    buttonName: '',
    hasCountdown: false,
    countdownDate: '',
    countdownTimeOnly: '',
    priority: 'normal'
  });

  const [categories, setCategories] = useState([]);
  const [products, setProducts] = useState([]);

  const [productSearch, setProductSearch] = useState('');
  const [productSearchTimeout, setProductSearchTimeout] = useState(null);

  const [tempSelectedCategory, setTempSelectedCategory] = useState('');
  const [tempSelectedSubCategory, setTempSelectedSubCategory] = useState('');
  const [tempSelectedProducts, setTempSelectedProducts] = useState([]);

  const [allSelectedProducts, setAllSelectedProducts] = useState([]);

  const fetchAllBanners = async () => {
    try {
      setLoadingBanners(true);
      const params = activeFilter !== 'all' ? { type: activeFilter } : {};
      const response = await bannerAPI.getAllBanners(params);
      if (response.success) {
        setBanners(response.data || []);
        if (response.counts) {
           setCounts(response.counts);
        }
      }
    } catch (error) {
      console.error('Error fetching banners:', error);
    } finally {
      setLoadingBanners(false);
    }
  };

  const fetchAllCategories = async () => {
    try {
      setLoadingCategories(true);
      const response = await categoryAPI.getCategories();

      if (response.success) {
        const transformedCategories = response.data.map((category) => ({
          id: category._id,
          title: category.title,
          arabicTitle: category.ar,
          image: category.image,
          isHidden: category.isHidden || false,
          subcategories: category.subCategories ? category.subCategories.map((sub) => ({
            id: sub._id,
            title: sub.title,
            arabicTitle: sub.ar,
            image: sub.image,
            isHidden: sub.isHidden || false,
            parentId: category._id
          })) : []
        }));
        setCategories(filterCategoriesByAccess(transformedCategories));
      }
    } catch (err) {
      console.error('Error fetching categories:', err);
    } finally {
      setLoadingCategories(false);
    }
  };

  const fetchProducts = async (searchQuery = '') => {
    try {
      setLoadingProducts(true);

      if (!searchQuery.trim() || searchQuery.trim().length < 3) {
        setProducts([]);
        setLoadingProducts(false);
        return;
      }

      const response = await productAPI.fetchProducts(1, searchQuery, 'all', 'all', 'all', 20);

      if (response.success) {
        setProducts(response.data.products);
      } else {
        setProducts([]);
      }
    } catch (err) {
      console.error('Error fetching products:', err);
      setProducts([]);
    } finally {
      setLoadingProducts(false);
    }
  };

  useEffect(() => {
    fetchAllBanners();
  }, [activeFilter]);

  useEffect(() => {
    if (isCategoryDialogOpen && categories.length === 0) {
      fetchAllCategories();
    }
  }, [isCategoryDialogOpen]);

  useEffect(() => {
    return () => {
      if (productSearchTimeout) {
        clearTimeout(productSearchTimeout);
      }
    };
  }, [productSearchTimeout]);

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        toast.error('Source image must be less than 5MB');
        return;
      }

      const reader = new FileReader();
      reader.onload = (readerEvent) => {
        setCropperSrc(readerEvent.target.result);
        setShowCropper(true);
      };
      reader.readAsDataURL(file);
    }
    e.target.value = '';
  };

  const handleCropComplete = (croppedFile, previewUrl) => {
    if (croppedFile.size > 600 * 1024) {
      toast.error('Cropped image size exceeds 600KB limit. Please try again.');
      return;
    }

    setFormData(prev => ({
      ...prev,
      image: croppedFile,
      imagePreview: previewUrl
    }));
    setShowCropper(false);
    setCropperSrc(null);
  };

  const handleCropperClose = () => {
    setShowCropper(false);
    setCropperSrc(null);
  };

  const handleIconChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Check if file is PNG
      if (file.type !== 'image/png') {
        toast.error('Icon must be a PNG file');
        return;
      }
      // Check file size (600KB limit)
      if (file.size > 600 * 1024) {
        toast.error('Icon size must be less than 600KB');
        return;
      }
      const reader = new FileReader();
      reader.onload = (e) => {
        setFormData(prev => ({
          ...prev,
          icon: file,
          iconPreview: e.target.result
        }));
      };
      reader.readAsDataURL(file);
    }
  };

  const handleTranslateTitle = async (text) => {
    if (!text || text.trim() === '') return;

    try {
      const response = await authAPI.translateText(text);
      if (response.success && response.data) {
        setFormData(prev => ({ ...prev, arabicTitle: response.data }));
      }
    } catch (error) {
      console.error('Translation failed:', error);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    if (name === 'type' && value === 'top_product') {
      setFormData(prev => ({
        ...prev,
        type: value,
        targetType: 'all',
        country: '',
        language: ''
      }));
      return;
    }
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleCountrySelect = (country) => {
    setFormData(prev => ({
      ...prev,
      country: country.id,
      language: ''
    }));
    setShowCountryDialog(false);
  };

  const handleLanguageSelect = (language) => {
    setFormData(prev => ({
      ...prev,
      language: language.id
    }));
    setShowLanguageDialog(false);
  };

  const handleProductToggle = (productId) => {
    setTempSelectedProducts(prev => {
      if (prev.includes(productId)) {
        return prev.filter(item => item !== productId);
      } else {
        return [...prev, productId];
      }
    });
  };

  const openCategoryDialog = () => {
    setTempSelectedCategory(formData.category);
    setTempSelectedSubCategory(formData.subCategory);
    setIsCategoryDialogOpen(true);
  };

  const openProductDialog = () => {
    setTempSelectedProducts([...formData.products]);
    setProductSearch('');
    setProducts([]);
    setIsProductDialogOpen(true);
  };

  const saveCategorySelection = () => {
    const catObj = categories.find(c => c.title === tempSelectedCategory);
    const subObj = catObj?.subcategories?.find(s => s.title === tempSelectedSubCategory);

    setFormData(prev => ({
      ...prev,
      category: catObj ? catObj.title : '',
      subCategory: subObj ? subObj.title : ''
    }));
    setIsCategoryDialogOpen(false);
  };

  const saveProductSelection = () => {
    setFormData(prev => ({
      ...prev,
      products: tempSelectedProducts
    }));

    setAllSelectedProducts(prev => {
      const map = new Map();

      prev.forEach(p => map.set(p.productId, p));

      products.forEach(p => {
        if (tempSelectedProducts.includes(p.productId)) {
          map.set(p.productId, p);
        }
      });

      return Array.from(map.values());
    });

    setIsProductDialogOpen(false);
  };

  const getSelectedCategoryName = () => {
    if (!formData.category) return 'No category selected';

    let displayText = `Category: ${capitalize(formData.category)}`;
    if (formData.subCategory) {
      displayText += ` (${capitalize(formData.subCategory)})`;
    }
    return displayText;
  };

  const removeSelectedProduct = (productId) => {
    setFormData(prev => ({
      ...prev,
      products: prev.products.filter(id => id !== productId)
    }));

    setAllSelectedProducts(prev =>
      prev.filter(p => p.productId !== productId)
    );
  };

  const getSelectedProductInfo = () => {
    // Return products in the order they appear in formData.products
    const selectedProducts = formData.products.map(id => 
      allSelectedProducts.find(p => p.productId === id)
    ).filter(Boolean);

    return {
      products: selectedProducts,
      count: selectedProducts.length,
      displayText: selectedProducts.length > 0
        ? `${selectedProducts.length} product${selectedProducts.length !== 1 ? 's' : ''} selected`
        : 'No products selected'
    };
  };

  const handleTargetTypeChange = (e) => {
    setFormData({
      ...formData,
      targetType: e.target.value,
      country: '',
      language: ''
    });
  };

  const handleSave = async () => {
    try {
      setSavingBanner(true);

      const isSlider = formData.type === 'banner_slider';
      const isPopupBanner = formData.type === 'popup_banner';

      if (!isSlider && !isPopupBanner && !formData.title.trim()) {
        toast.error('Please enter a banner title');
        return;
      }

      if (!isSlider && !isPopupBanner && !formData.arabicTitle.trim()) {
        toast.error('Please enter a banner arabic title');
        return;
      }

      if (formData.type !== 'top_product' && !formData.image && !formData.imagePreview) {
        toast.error('Please select a banner image');
        return;
      }

      if (isPopupBanner) {
        if (formData.hasButton && !formData.buttonName.trim()) {
          toast.error('Please enter a button name');
          return;
        }
        if (formData.hasCountdown) {
          if (!formData.countdownDate || !formData.countdownTimeOnly) {
            toast.error('Please select both countdown end date and time');
            return;
          }
          const combinedDateTime = new Date(`${formData.countdownDate}T${formData.countdownTimeOnly}`);
          if (isNaN(combinedDateTime.getTime())) {
            toast.error('Please enter a valid countdown end date and time');
            return;
          }
          if (combinedDateTime <= new Date()) {
            toast.error('Countdown end time must be in the future');
            return;
          }
        }
      }

      if (formData.applicationType === 'selected_categories' && !formData.category) {
        toast.error('Please select a category');
        return;
      }

      if (formData.applicationType === 'selected_products' && formData.products.length < 6) {
        toast.error('Please select at least 6 products');
        return;
      }

      if (formData.targetType === 'nationality' && !formData.country) {
        toast.error('Please select a country');
        return;
      }

      const payload = { 
        ...formData,
        country: formData.country ? String(formData.country).toLowerCase() : '',
        language: formData.language ? String(formData.language).toLowerCase() : '',
        countdownTime: formData.hasCountdown && formData.countdownDate && formData.countdownTimeOnly 
          ? new Date(`${formData.countdownDate}T${formData.countdownTimeOnly}`).toISOString() 
          : ''
      };

      if (payload.applicationType === 'selected_categories') {
        payload.products = [];
        setAllSelectedProducts([]);
      } else if (payload.applicationType === 'selected_products') {
        payload.category = "";
        payload.subCategory = "";
      } else if (payload.applicationType === 'none') {
        payload.products = [];
        payload.category = "";
        payload.subCategory = "";
        setAllSelectedProducts([]);
      }

      let response;

      if (editingBanner) {
        response = await bannerAPI.updateBanner(editingBanner._id, payload);
      } else {
        response = await bannerAPI.createBanner(payload);
      }

      if (response.success) {
        toast.success(`Banner ${editingBanner ? 'updated' : 'created'} successfully!`);
        handleCloseDialog();
        await fetchAllBanners();
      } else {
        toast.error(response.msg || `Failed to ${editingBanner ? 'update' : 'create'} banner`);
      }
    } catch (error) {
      console.error('Error saving banner:', error);
      toast.error(`Failed to ${editingBanner ? 'update' : 'create'} banner. Please try again.`);
    } finally {
      setSavingBanner(false);
    }
  };

  const handleEditBanner = (banner) => {
    setEditingBanner(banner);
    const isProductBanner = Array.isArray(banner.products) && banner.products.length > 0;
    const hasCategory = !!banner.category;

    let appType = 'none';
    if (isProductBanner) {
      appType = 'selected_products';
    } else if (hasCategory) {
      appType = 'selected_categories';
    } else if (banner.type !== 'banner_slider' && banner.type !== 'popup_banner') {
      appType = 'selected_categories';
    }

    const getLocalDateAndTime = (dateString) => {
      if (!dateString) return { date: '', time: '' };
      const d = new Date(dateString);
      if (isNaN(d.getTime())) return { date: '', time: '' };
      const offset = d.getTimezoneOffset();
      const localD = new Date(d.getTime() - (offset * 60 * 1000));
      const isoStr = localD.toISOString();
      return {
        date: isoStr.slice(0, 10),
        time: isoStr.slice(11, 16)
      };
    };

    const { date: cDate, time: cTime } = getLocalDateAndTime(banner.countdownTime);

    setFormData({
      title: banner.title || '',
      arabicTitle: banner.arTitle || '',
      type: banner.type || 'deals_promotions',
      image: null,
      imagePreview: banner.image,
      icon: null,
      iconPreview: banner.icon || null,
      description: banner.description || '',
      color: banner.color || '',
      applicationType: appType,
      category: banner.category || '',
      subCategory: banner.subCategory || '',
      products: isProductBanner ? banner.products.map(p => p.productId) : [],
      targetType: banner.country ? 'nationality' : 'all',
      country: banner.country || '',
      language: banner.language || '',
      hasButton: banner.hasButton || false,
      buttonName: banner.buttonName || '',
      hasCountdown: banner.hasCountdown || false,
      countdownDate: cDate,
      countdownTimeOnly: cTime,
      priority: banner.priority || 'normal'
    });

    setAllSelectedProducts(isProductBanner ? banner.products : []);
    setShowDialog(true);
  };

  const handleDeleteBanner = (bannerId) => {
    setBannerToDelete(bannerId);
    setShowDeleteDialog(true);
  };

  const confirmDelete = async () => {
    if (!bannerToDelete) return;

    try {
      setDeleteLoading(true);
      const response = await bannerAPI.deleteBanner(bannerToDelete);

      if (response.success) {
        toast.success('Banner deleted successfully!');
        fetchAllBanners();
        setShowDeleteDialog(false);
        setBannerToDelete(null);
      } else {
        toast.error(response.msg || response.error || 'Failed to delete banner');
      }
    } catch (error) {
      console.error('Error deleting banner:', error);
      toast.error('Failed to delete banner');
    } finally {
      setDeleteLoading(false);
    }
  };

  const cancelDelete = () => {
    setShowDeleteDialog(false);
    setBannerToDelete(null);
  };

  const handleShareBanner = (banner) => {
    const domain = IS_ALFAJR ? 'fajr.qa' : 'ablemartonline.com';
    const url = `https://${domain}/custom-listing/${banner.type}/${banner._id}`;
    navigator.clipboard.writeText(url)
      .then(() => {
        toast.success("Banner link copied!");
      })
      .catch((err) => {
        console.error("Failed to copy link:", err);
        toast.error("Failed to copy link");
      });
  };

  const handleCloseDialog = () => {
    setShowDialog(false);
    setEditingBanner(null);
    setProductSearch('');
    setProducts([]);
    setFormData({
      title: '',
      arabicTitle: '',
      type: 'deals_promotions',
      image: null,
      imagePreview: null,
      icon: null,
      iconPreview: null,
      description: '',
      color: '',
      applicationType: 'selected_categories',
      category: '',
      subCategory: '',
      products: [],
      targetType: 'all',
      country: '',
      language: '',
      hasButton: false,
      buttonName: '',
      hasCountdown: false,
      countdownDate: '',
      countdownTimeOnly: '',
      priority: 'normal'
    });
  };

  const handleProductSearch = (e) => {
    const searchValue = e.target.value;
    setProductSearch(searchValue);

    if (productSearchTimeout) {
      clearTimeout(productSearchTimeout);
    }

    const newTimeout = setTimeout(() => {
      fetchProducts(searchValue);
    }, 500);

    setProductSearchTimeout(newTimeout);
  };

  const handleSaveReorder = async () => {
    try {
      const reorderData = {
        banners: banners
          .map((banner, index) => ({ _id: banner._id, order: index + 1, oldOrder: banner.order }))
          .filter(banner => banner.order !== banner.oldOrder)
      };

      if (reorderData.banners.length === 0) {
        toast.info("No changes to save");
        setIsReorderMode(false);
        return;
      }

      const response = await bannerAPI.reorderBanners(reorderData);
      if (response.success) {
        setBanners(response.data || []);
        toast.success('Banners reordered successfully!');
        setIsReorderMode(false);
      } else {
        toast.error(response.msg || response.error || 'Failed to reorder banners');
      }
    } catch (error) {
      console.error('Error saving reorder:', error);
      toast.error('Failed to reorder banners. Please try again.');
    } finally {
      setIsReorderMode(false);
    }
  };

  const handleExitReorderMode = () => {
    setIsReorderMode(false);
    fetchAllBanners();
  };

  const handleDragStart = (e, bannerId) => {
    setDraggedBanner(bannerId);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e, bannerId) => {
    e.preventDefault();
    if (draggedBanner !== bannerId) {
      setDragOverBanner(bannerId);
    }
  };

  const handleDragLeave = () => {
    setDragOverBanner(null);
  };

  const handleDrop = (e, targetBannerId) => {
    e.preventDefault();
    if (draggedBanner && draggedBanner !== targetBannerId) {
      const draggedIndex = banners.findIndex(b => b._id === draggedBanner);
      const targetIndex = banners.findIndex(b => b._id === targetBannerId);

      const newBanners = [...banners];
      const [movedBanner] = newBanners.splice(draggedIndex, 1);
      newBanners.splice(targetIndex, 0, movedBanner);

      setBanners(newBanners);
    }
    setDraggedBanner(null);
    setDragOverBanner(null);
  };

  const handleDragEnd = () => {
    setDraggedBanner(null);
    setDragOverBanner(null);
  };

  const handleProductDragStart = (e, productId) => {
    setDraggedProduct(productId);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleProductDragOver = (e, productId) => {
    e.preventDefault();
    if (draggedProduct !== productId) {
      setDragOverProduct(productId);
    }
  };

  const handleProductDrop = (e, targetProductId) => {
    e.preventDefault();
    if (draggedProduct && draggedProduct !== targetProductId) {
      const draggedIndex = formData.products.indexOf(draggedProduct);
      const targetIndex = formData.products.indexOf(targetProductId);

      const newProducts = [...formData.products];
      const [movedProductId] = newProducts.splice(draggedIndex, 1);
      newProducts.splice(targetIndex, 0, movedProductId);

      setFormData(prev => ({
        ...prev,
        products: newProducts
      }));
    }
    setDraggedProduct(null);
    setDragOverProduct(null);
  };

  const handleProductDragEnd = () => {
    setDraggedProduct(null);
    setDragOverProduct(null);
  };

  const capitalize = (str = '') => str ? str.charAt(0).toUpperCase() + str.slice(1) : '';

  return (
    <div className="banner-products-container">
      <div className="banner-page-header">
        <div className="banner-page-title">
          <h1>Banners & Sections</h1>
          <p>Create and manage banners & sections</p>
        </div>
        <div className="banner-header-actions">
          {isReorderMode ? (
            <div className="banner-reorder-actions">
              <button
                className="banner-save-changes-btn"
                onClick={handleSaveReorder}
              >
                <Save size={20} />
                Save Changes
              </button>
              <button
                className="banner-exit-reorder-btn"
                onClick={handleExitReorderMode}
              >
                <X size={20} />
                Exit Reorder
              </button>
            </div>
          ) : (
            <div className="banner-normal-actions">
              {canEdit && activeFilter === 'all' && (
                <button
                  className="banner-reorder-mode-btn"
                  onClick={() => setIsReorderMode(true)}
                >
                  <GripVertical size={20} />
                  Reorder
                </button>
              )}
              {canCreate && (
                <button
                  className="banner-create-btn"
                  onClick={() => setShowDialog(true)}
                >
                  <Plus size={20} />
                  Create New Banner
                </button>
              )}
            </div>
          )}
        </div>
      </div>

      {!isReorderMode && (
        <div className="banner-filters-row">
          <button
            className={`banner-filter-pill ${activeFilter === 'all' ? 'active' : ''}`}
            onClick={() => setActiveFilter('all')}
          >
            All ({counts.all})
          </button>
          <button
            className={`banner-filter-pill ${activeFilter === 'deals_promotions' ? 'active' : ''}`}
            onClick={() => setActiveFilter('deals_promotions')}
          >
            Banner + products section ({counts.deals_promotions})
          </button>
          <button
            className={`banner-filter-pill ${activeFilter === 'banner_slider' ? 'active' : ''}`}
            onClick={() => setActiveFilter('banner_slider')}
          >
            Top Banner Slider ({counts.banner_slider})
          </button>
          <button
            className={`banner-filter-pill ${activeFilter === 'top_product' ? 'active' : ''}`}
            onClick={() => setActiveFilter('top_product')}
          >
            Products only section ({counts.top_product})
          </button>
          {IS_ALFAJR && (
            <button
              className={`banner-filter-pill ${activeFilter === 'category_screen_banners' ? 'active' : ''}`}
              onClick={() => setActiveFilter('category_screen_banners')}
            >
              Category Screen Banners ({counts.category_screen_banners})
            </button>
          )}
          <button
            className={`banner-filter-pill ${activeFilter === 'popup_banner' ? 'active' : ''}`}
            onClick={() => setActiveFilter('popup_banner')}
          >
            Popup Banners ({counts.popup_banner || 0})
          </button>
        </div>
      )}

      {isReorderMode && (
        <div className="banner-reorder-instructions">
          <p>💡 Drag and drop banners to reorder them. Click "Save Changes" when you're done.</p>
        </div>
      )}

      <div className="banner-banners-list-section">
        {loadingBanners ? (
          <div className="banner-loading-state">
            <div className="banner-loading-spinner"></div>
            <p>Loading banners...</p>
          </div>
        ) : banners.length === 0 ? (
          <div className="banner-empty-state">
            <p>No banners created yet</p>
          </div>
        ) : (
          <div className="banner-banners-grid">
            {banners.map(banner => (
              <div
                key={banner._id}
                className={`banner-card ${isReorderMode ? 'reorder-mode' : ''} ${draggedBanner === banner._id ? 'dragging' : ''} ${dragOverBanner === banner._id ? 'drag-over' : ''}`}
                draggable={isReorderMode}
                onDragStart={(e) => handleDragStart(e, banner._id)}
                onDragOver={(e) => handleDragOver(e, banner._id)}
                onDragLeave={handleDragLeave}
                onDrop={(e) => handleDrop(e, banner._id)}
                onDragEnd={handleDragEnd}
              >
                {isReorderMode && (
                  <div className="banner-drag-handle">
                    <GripVertical size={20} />
                  </div>
                )}
                <div className="banner-image">
                  {banner.image ? (
                    <img src={banner.image} alt={banner.title} />
                  ) : (
                    <div className="banner-no-image-placeholder">
                      <ImageIcon size={32} />
                      <span>No Image</span>
                    </div>
                  )}
                </div>
                <div className="banner-info">
                  <div className="banner-top-section">
                    <div className="banner-header">
                      <div className="banner-title-wrapper">
                        <h3 className="banner-title">{banner.title || '-'}</h3>
                      </div>

                      {!isReorderMode && (
                        <div className="banner-actions">
                          <button
                            className="banner-share-btn"
                            onClick={() => handleShareBanner(banner)}
                            title="Share banner link"
                          >
                            <Share2 size={14} />
                          </button>
                          {canEdit && (
                            <button
                              className="banner-edit-btn"
                              onClick={() => handleEditBanner(banner)}
                            >
                              <Edit size={14} />
                            </button>
                          )}
                          {canDelete && (
                            <button
                              className="banner-delete-btn"
                              onClick={() => handleDeleteBanner(banner._id)}
                            >
                              <Trash size={14} />
                            </button>
                          )}
                        </div>
                      )}
                    </div>

                    <div className="banner-subheader">
                      {banner.type && (
                        <div className={`banner-type-badge ${
                          banner.type === 'banner_slider' ? 'slider' :
                          banner.type === 'top_product' ? 'top-product' :
                          banner.type === 'category_screen_banners' ? 'category-screen' :
                          banner.type === 'popup_banner' ? 'popup-banner' :
                          'deals'
                        }`}>
                          {
                            banner.type === 'banner_slider' ? 'Top Banner Slider' :
                            banner.type === 'top_product' ? 'Products only' :
                            banner.type === 'category_screen_banners' ? 'Category Screen Banners' :
                            banner.type === 'popup_banner' ? 'Popup Banner' :
                            'Banner + products'
                          }
                        </div>
                      )}

                      {banner.arTitle && (
                        <p className="banner-arabic-title">{banner.arTitle}</p>
                      )}
                    </div>
                  </div>

                  <div className="banner-meta-info">
                    {banner.type === 'popup_banner' && (
                      <>
                        <div className="banner-meta-item">
                          <strong>Priority:</strong> <span style={{ color: banner.priority === 'high' ? '#e53e3e' : '#4a5568', fontWeight: banner.priority === 'high' ? 'bold' : 'normal' }}>{capitalize(banner.priority || 'normal')}</span>
                        </div>
                        {banner.hasButton && (
                          <div className="banner-meta-item">
                            <strong>Button:</strong> {banner.buttonName || 'No label'}
                          </div>
                        )}
                        {banner.hasCountdown && banner.countdownTime && (
                          <div className="banner-meta-item">
                            <strong>Countdown:</strong> {helpers.formatQatarTime(banner.countdownTime)}
                          </div>
                        )}
                      </>
                    )}

                    {banner.category && (
                      <div className="banner-meta-item">
                        <strong>Category:</strong> {capitalize(banner.category)}
                        {banner.subCategory && ` (${capitalize(banner.subCategory)})`}
                      </div>
                    )}

                    {banner.products?.length > 0 && (
                      <div className="banner-meta-item">
                        <strong>Products:</strong> {banner.products.length}
                      </div>
                    )}

                    {banner.country && (
                      <div className="banner-meta-item">
                        <strong>Country:</strong> {getCountryName(banner.country)}
                      </div>
                    )}

                    {banner.language && (
                      <div className="banner-meta-item">
                        <strong>Language:</strong> {getLanguageName(banner.language)}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {showDialog && (
          <div className="banner-dialog-overlay">
            <div className="banner-dialog">
              <div className="banner-dialog-header">
                <h2>{editingBanner ? 'Edit Banner' : 'Create Banner'}</h2>
                <button className="banner-dialog-close" onClick={handleCloseDialog}>
                  <X size={20} />
                </button>
              </div>

              <div className="banner-dialog-content">
                <div className="banner-form-group">
                  <label htmlFor="bannerType">Banner Type</label>
                  <select
                    id="bannerType"
                    name="type"
                    value={formData.type}
                    onChange={(e) => {
                      const newType = e.target.value;
                      handleInputChange(e);
                      if (newType === 'banner_slider') {
                        setFormData(prev => ({ ...prev, type: newType, applicationType: 'none', products: [], category: '', subCategory: '' }));
                      } else if (formData.applicationType === 'none') {
                        setFormData(prev => ({ ...prev, type: newType, applicationType: 'selected_categories' }));
                      }
                    }}
                    className="banner-form-select"
                  >
                    <option value="deals_promotions">Banner + products section</option>
                    <option value="banner_slider">Top Banner Slider</option>
                    <option value="top_product">Products only section</option>
                    {IS_ALFAJR && (
                      <option value="category_screen_banners">Category Screen Banners</option>
                    )}
                    <option value="popup_banner">Popup Banner</option>
                  </select>
                </div>

                <div className="banner-form-group">
                  <label htmlFor="bannerTitle">Title {['banner_slider', 'popup_banner'].includes(formData.type) ? '(Optional)' : '*'}</label>
                  <input
                    type="text"
                    name="title"
                    value={formData.title}
                    onChange={handleInputChange}
                    onBlur={(e) => handleTranslateTitle(e.target.value)}
                    placeholder="Enter banner title"
                    className="banner-form-input"
                  />
                </div>

                <div className="banner-form-group">
                  <label htmlFor="categoryArabicName">Arabic Name {['banner_slider', 'popup_banner'].includes(formData.type) ? '(Optional)' : '*'}</label>
                  <input
                    type="text"
                    id="bannerArabic"
                    value={formData.arabicTitle}
                    onChange={(e) => setFormData({ ...formData, arabicTitle: e.target.value })}
                    placeholder="أدخل اسم الفئة"
                    className="banner-form-input"
                    dir="rtl"
                  />
                </div>

                {formData.type !== 'top_product' && (
                  <div className="banner-form-group">
                    <label htmlFor="bannerImage">Banner Image {formData.type === 'popup_banner' ? '(4:5 ratio)' : '(3:1 ratio)'}</label>
                    <div className="banner-image-upload-section">
                      <div className="banner-image-preview-container">
                        {formData.imagePreview ? (
                          <div className="banner-image-preview">
                            <img src={formData.imagePreview} alt="Banner preview" />
                          </div>
                        ) : (
                          <div className="banner-image-placeholder">
                            <ImageIcon size={32} />
                            <span>No image selected</span>
                          </div>
                        )}
                      </div>

                      <div className="banner-image-upload-controls">
                        <input
                          type="file"
                          id="bannerImage"
                          accept="image/*"
                          onChange={handleImageChange}
                          className="banner-file-input"
                        />
                        <label htmlFor="bannerImage" className="banner-upload-btn">
                          <Upload size={16} />
                          {formData.imagePreview ? 'Change Image' : 'Choose Image'}
                        </label>
                      </div>
                      <div className="banner-file-size-limit">
                        Maximum size: 600KB {formData.type === 'popup_banner' ? '(4:5 ratio recommended)' : '(3:1 ratio recommended)'}
                      </div>
                    </div>
                  </div>
                )}

                {/* Color: Hide for popup_banner, show only on AbleMart for others */}
                {!IS_ALFAJR && formData.type !== 'popup_banner' && (
                  <div className="banner-form-group">
                    <label htmlFor="bannerColor">Color</label>
                    <div className="banner-color-input-wrapper">
                      <input
                        type="color"
                        id="bannerColorPicker"
                        value={formData.color || '#000000'}
                        onChange={(e) => setFormData(prev => ({ ...prev, color: e.target.value }))}
                        className="banner-color-picker"
                      />
                      <input
                        type="text"
                        id="bannerColor"
                        name="color"
                        value={formData.color}
                        onChange={handleInputChange}
                        placeholder="#000000"
                        className="banner-form-input banner-color-text"
                      />
                      {formData.color && (
                        <button
                          type="button"
                          className="banner-clear-color-btn"
                          onClick={() => setFormData(prev => ({ ...prev, color: '' }))}
                          title="Clear color"
                        >
                          <X size={14} />
                        </button>
                      )}
                    </div>
                  </div>
                )}

                {/* Icon: Show only on AbleMart for non-popup banners */}
                {!IS_ALFAJR && formData.type !== 'popup_banner' && (
                  <div className="banner-form-group">
                    <label htmlFor="bannerIcon">Icon (PNG only)</label>
                    <div className="banner-image-upload-section">
                      <div className="banner-icon-preview-container">
                        {formData.iconPreview ? (
                          <div className="banner-icon-preview">
                            <img src={formData.iconPreview} alt="Icon preview" />
                            <button
                              type="button"
                              className="banner-clear-icon-btn"
                              onClick={() => setFormData(prev => ({
                                ...prev,
                                icon: null,
                                iconPreview: null,
                                clearIcon: true
                              }))}
                              title="Remove icon"
                            >
                              <X size={14} />
                            </button>
                          </div>
                        ) : (
                          <div className="banner-icon-placeholder">
                            <ImageIcon size={24} />
                            <span>No icon</span>
                          </div>
                        )}
                      </div>

                      <div className="banner-image-upload-controls">
                        <input
                          type="file"
                          id="bannerIcon"
                          accept="image/png"
                          onChange={handleIconChange}
                          className="banner-file-input"
                        />
                        <label htmlFor="bannerIcon" className="banner-upload-btn">
                          <Upload size={16} />
                          {formData.iconPreview ? 'Change Icon' : 'Choose Icon'}
                        </label>
                      </div>
                      <div className="banner-file-size-limit">
                        PNG only, max 600KB
                      </div>
                    </div>
                  </div>
                )}

                {/* Description: Show only on AbleMart */}
                {!IS_ALFAJR && (
                  <div className="banner-form-group">
                    <label htmlFor="bannerDescription">Description</label>
                    <textarea
                      id="bannerDescription"
                      name="description"
                      value={formData.description}
                      onChange={handleInputChange}
                      placeholder="Enter banner description"
                      className="banner-form-textarea"
                      rows={3}
                    />
                  </div>
                )}

                {/* Popup Banner Settings: Button, Countdown, Priority */}
                {formData.type === 'popup_banner' && (
                  <>
                    <div className="banner-form-group">
                      <label>Button</label>
                      <div className="banner-radio-group">
                        <label className="banner-radio-label">
                          <input
                            type="radio"
                            name="hasButton"
                            checked={!formData.hasButton}
                            onChange={() => setFormData(prev => ({ ...prev, hasButton: false, buttonName: '' }))}
                          />
                          No Button
                        </label>
                        <label className="banner-radio-label">
                          <input
                            type="radio"
                            name="hasButton"
                            checked={formData.hasButton}
                            onChange={() => setFormData(prev => ({ ...prev, hasButton: true }))}
                          />
                          Have Button
                        </label>
                      </div>
                    </div>

                    {formData.hasButton && (
                      <div className="banner-form-group">
                        <label htmlFor="bannerButtonName">Button Name *</label>
                        <input
                          type="text"
                          id="bannerButtonName"
                          name="buttonName"
                          value={formData.buttonName}
                          onChange={handleInputChange}
                          placeholder="Enter button name (e.g. Shop Now)"
                          className="banner-form-input"
                        />
                      </div>
                    )}

                    <div className="banner-form-group">
                      <label>Countdown</label>
                      <div className="banner-radio-group">
                        <label className="banner-radio-label">
                          <input
                            type="radio"
                            name="hasCountdown"
                            checked={!formData.hasCountdown}
                            onChange={() => setFormData(prev => ({ ...prev, hasCountdown: false, countdownDate: '', countdownTimeOnly: '' }))}
                          />
                          No Countdown
                        </label>
                        <label className="banner-radio-label">
                          <input
                            type="radio"
                            name="hasCountdown"
                            checked={formData.hasCountdown}
                            onChange={() => setFormData(prev => ({ ...prev, hasCountdown: true }))}
                          />
                          Have Countdown
                        </label>
                      </div>
                    </div>

                    {formData.hasCountdown && (
                      <div className="banner-form-group">
                        <label>Countdown End Time (Future Date & Time) *</label>
                        <div style={{ display: 'flex', gap: '16px' }}>
                          <input
                            type="date"
                            name="countdownDate"
                            value={formData.countdownDate}
                            onChange={handleInputChange}
                            className="banner-form-input"
                            min={new Date().toISOString().slice(0, 10)}
                            style={{ flex: 1 }}
                          />
                          <input
                            type="time"
                            name="countdownTimeOnly"
                            value={formData.countdownTimeOnly}
                            onChange={handleInputChange}
                            className="banner-form-input"
                            style={{ flex: 1 }}
                          />
                        </div>
                      </div>
                    )}

                    <div className="banner-form-group">
                      <label htmlFor="bannerPriority">Priority</label>
                      <select
                        id="bannerPriority"
                        name="priority"
                        value={formData.priority}
                        onChange={handleInputChange}
                        className="banner-form-select"
                      >
                        <option value="normal">Normal</option>
                        <option value="high">High Priority</option>
                      </select>
                    </div>
                  </>
                )}

                <div className="banner-form-group">
                  <label htmlFor="applicationType">Application Type</label>
                  <div className="banner-radio-group">
                    {formData.type === 'banner_slider' && (
                      <label className="banner-radio-label">
                        <input
                          type="radio"
                          name="applicationType"
                          value="none"
                          checked={formData.applicationType === 'none'}
                          onChange={handleInputChange}
                        />
                        None
                      </label>
                    )}
                    <label className="banner-radio-label">
                      <input
                        type="radio"
                        name="applicationType"
                        value="selected_categories"
                        checked={formData.applicationType === 'selected_categories'}
                        onChange={handleInputChange}
                      />
                      Select Category
                    </label>
                    <label className="banner-radio-label">
                      <input
                        type="radio"
                        name="applicationType"
                        value="selected_products"
                        checked={formData.applicationType === 'selected_products'}
                        onChange={handleInputChange}
                      />
                      Select Products
                    </label>
                  </div>
                </div>

                {formData.applicationType === 'selected_categories' && (
                  <div className="banner-form-group">
                    <div className='banner-choose-btn-container'>
                      <button
                        type="button"
                        className="banner-choose-btn"
                        onClick={openCategoryDialog}
                      >
                        Choose Category
                      </button>
                    </div>
                    <div className="banner-selected-items">
                      {getSelectedCategoryName()}
                    </div>
                  </div>
                )}

                {formData.applicationType === 'selected_products' && (
                  <div className="banner-form-group">
                    <div className='banner-choose-btn-container'>
                      <button
                        type="button"
                        className="banner-choose-btn"
                        onClick={openProductDialog}
                      >
                        Choose Products
                      </button>
                    </div>
                    <div className="banner-selected-products-section">
                      <div className="banner-selected-products-header">
                        <span className="banner-products-count">
                          {getSelectedProductInfo().displayText}
                        </span>
                        {getSelectedProductInfo().count > 0 && (
                          <button
                            type="button"
                            className="banner-view-products-btn"
                            onClick={() => setIsProductViewDialogOpen(true)}
                          >
                            View Products
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                {formData.type !== 'top_product' && (
                  <>
                    <div className="banner-form-group">
                      <label htmlFor="targetType">Target Type</label>
                      <select
                        id="targetType"
                        value={formData.targetType}
                        onChange={handleTargetTypeChange}
                        className="banner-form-select"
                      >
                        <option value="all">All</option>
                        <option value="nationality">Based on nationality</option>
                      </select>
                    </div>

                    {formData.targetType === 'nationality' && (
                      <>
                        <div className="banner-form-group">
                          <label>Nationality</label>
                          <button
                            className="banner-choose-country-btn"
                            onClick={() => setShowCountryDialog(true)}
                          >
                            <Globe size={16} />
                            {formData.country ? getCountryName(formData.country) : 'Choose Country'}
                          </button>
                        </div>

                        {formData.country === 'india' && (
                          <div className="banner-form-group">
                            <label>Language</label>
                            <button
                              className="banner-choose-language-btn"
                              onClick={() => setShowLanguageDialog(true)}
                            >
                              <Languages size={16} />
                              {formData.language ? getLanguageName(formData.language) : 'Choose Language'}
                            </button>
                          </div>
                        )}
                      </>
                    )}
                  </>
                )}
              </div>

              <div className="banner-dialog-footer">
                <button className="banner-cancel-btn" onClick={handleCloseDialog}>
                  Cancel
                </button>
                <button
                  className="banner-save-btn"
                  onClick={handleSave}
                  disabled={savingBanner}
                >
                  {savingBanner ? (editingBanner ? 'Updating...' : 'Creating...') : (editingBanner ? 'Update Banner' : 'Create Banner')}
                </button>
              </div>
            </div>
          </div>
        )}

        {isCategoryDialogOpen && (
          <div className="banner-dialog-overlay">
            <div className="banner-category-selection-dialog">
              <div className="banner-dialog-header">
                <h2>Select Category</h2>
                <button className="banner-close-btn" onClick={() => setIsCategoryDialogOpen(false)}>
                  <X size={20} />
                </button>
              </div>

              <div className="banner-dialog-content">
                {loadingCategories ? (
                  <div className="banner-loading-state">
                    <div className="banner-loading-spinner" />
                    <p>Loading categories...</p>
                  </div>
                ) : (
                  <div className="banner-form-group-row">
                    <div className="banner-form-group">
                      <label>Category</label>
                      <select
                        value={tempSelectedCategory}
                        onChange={(e) => {
                          setTempSelectedCategory(e.target.value);
                          setTempSelectedSubCategory('');
                        }}
                        className="banner-form-select"
                      >
                        <option value="">Select Category</option>
                        {categories.map(category => (
                          <option key={category.id} value={category.title}>{category.title}</option>
                        ))}
                      </select>
                    </div>

                    <div className="banner-form-group">
                      <label>Sub Category</label>
                      <select
                        value={tempSelectedSubCategory}
                        onChange={(e) => setTempSelectedSubCategory(e.target.value)}
                        className="banner-form-select"
                        disabled={!tempSelectedCategory}
                      >
                        <option value="">Select Sub Category</option>
                        {categories
                          .find(cat => cat.title === tempSelectedCategory)
                          ?.subcategories?.map(sub => (
                            <option key={sub.id} value={sub.title}>
                              {sub.title}
                            </option>
                          ))}
                      </select>
                    </div>
                  </div>
                )}
              </div>

              <div className="banner-category-selection-footer">
                <button className="banner-cancel-btn" onClick={() => setIsCategoryDialogOpen(false)}>
                  Cancel
                </button>
                <button className="banner-save-btn" onClick={saveCategorySelection}>
                  Save Selection
                </button>
              </div>
            </div>
          </div>
        )}

        {isProductDialogOpen && (
          <div className="banner-dialog-overlay">
            <div className="banner-dialog banner-selection-dialog">
              <div className="banner-dialog-header">
                <h2>Select Products</h2>
                <button className="banner-close-btn" onClick={() => setIsProductDialogOpen(false)}>
                  <X size={20} />
                </button>
              </div>

              <div className="banner-search-section">
                <input
                  type="text"
                  placeholder="Enter at least 3 characters to search products"
                  value={productSearch}
                  onChange={handleProductSearch}
                  className="banner-search-input"
                />
              </div>

              <div className="banner-selection-content">
                {loadingProducts ? (
                  <div className="banner-loading-state">
                    <div className="banner-loading-spinner"></div>
                    <p>Searching products...</p>
                  </div>
                ) : products.length === 0 ? (
                  <div className="banner-empty-state">
                    <p>Enter product name to search</p>
                  </div>
                ) : (
                  <div className="banner-products-grid">
                    {[...products].sort((a, b) => {
                      const aSelected = formData.products.includes(a.productId) ? 1 : 0;
                      const bSelected = formData.products.includes(b.productId) ? 1 : 0;
                      return bSelected - aSelected;
                    }).map(product => (
                      <label key={product._id} className="banner-product-checkbox">
                        <input
                          type="checkbox"
                          checked={tempSelectedProducts.includes(product.productId)}
                          onChange={() => handleProductToggle(product.productId)}
                        />
                        <div className="banner-product-content">
                          <div className="banner-product-image">
                            {product.images && product.images.length > 0 ? (
                              <img src={product.images[0].image} alt={product.name} />
                            ) : product.image ? (
                              <img src={product.image} alt={product.name} />
                            ) : (
                              <div className="banner-no-image">No Image</div>
                            )}
                          </div>
                          <div className="banner-product-details">
                            <span className="banner-product-name">{product.name}</span>
                            <div className="banner-product-meta">
                              <span className="banner-product-category">{product.category}</span>
                              {product.rate && (
                                <span className="banner-product-rate">₹{product.rate}</span>
                              )}
                            </div>
                          </div>
                        </div>
                      </label>
                    ))}
                  </div>
                )}
              </div>

              <div className="banner-selection-dialog-footer">
                <div className="banner-selection-stats">
                  Selected: {tempSelectedProducts.length} products
                </div>
                <div className="banner-form-actions">
                  <button type="button" className="banner-cancel-btn" onClick={() => setIsProductDialogOpen(false)}>
                    Cancel
                  </button>
                  <button type="button" className="banner-submit-btn" onClick={saveProductSelection}>
                    Save Selection
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {isProductViewDialogOpen && (
          <div className="banner-dialog-overlay">
            <div className="banner-dialog banner-product-view-dialog">
              <div className="banner-dialog-header">
                <h2>Selected Products ({getSelectedProductInfo().count})</h2>
                <button
                  className="banner-close-btn"
                  onClick={() => setIsProductViewDialogOpen(false)}
                >
                  <X size={20} />
                </button>
              </div>

              <div className="banner-selected-products-list">
                {getSelectedProductInfo().products.length === 0 ? (
                  <div className="banner-empty-state">
                    <p>No products selected</p>
                  </div>
                ) : (
                  <div className="banner-products-list">
                    {getSelectedProductInfo().products.map(product => (
                      <div
                        key={product._id}
                        className={`banner-selected-product-item ${draggedProduct === product.productId ? 'dragging' : ''} ${dragOverProduct === product.productId ? 'drag-over' : ''}`}
                        draggable
                        onDragStart={(e) => handleProductDragStart(e, product.productId)}
                        onDragOver={(e) => handleProductDragOver(e, product.productId)}
                        onDragLeave={() => setDragOverProduct(null)}
                        onDrop={(e) => handleProductDrop(e, product.productId)}
                        onDragEnd={handleProductDragEnd}
                      >
                        <div className="banner-product-drag-handle">
                          <GripVertical size={16} />
                        </div>
                        <div className="banner-product-content">
                          <div className="banner-product-image">
                            {product.images && product.images.length > 0 ? (
                              <img src={product.images[0].image} alt={product.name} />
                            ) : product.image ? (
                              <img src={product.image} alt={product.name} />
                            ) : (
                              <div className="banner-no-image">No Image</div>
                            )}
                          </div>
                          <div className="banner-product-details">
                            <span className="banner-product-name">{product.name}</span>
                            <span className="banner-product-category">{product.category}</span>
                          </div>
                        </div>
                        <button
                          type="button"
                          className="banner-remove-product-btn"
                          onClick={() => removeSelectedProduct(product.productId)}
                          title="Remove product"
                        >
                          <Trash size={16} />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="coupon-selection-dialog-footer" style={{ padding: '30px' }}>
              </div>
            </div>
          </div>
        )}

        {showCropper && (
          <ImageCropperModal
            imageSrc={cropperSrc}
            onCropComplete={handleCropComplete}
            onClose={handleCropperClose}
            aspectRatio={formData.type === 'popup_banner' ? 0.8 : 3}
          />
        )}

        <CountrySelector
          isOpen={showCountryDialog}
          onClose={() => setShowCountryDialog(false)}
          onSelect={handleCountrySelect}
          selectedCountryId={formData.country}
          title="Select Country"
        />

        <LanguageSelector
          isOpen={showLanguageDialog}
          onClose={() => setShowLanguageDialog(false)}
          onSelect={handleLanguageSelect}
          selectedLanguageId={formData.language}
          title="Select Language"
        />

        {showDeleteDialog && (
          <div className="banner-dialog-overlay">
            <div className="banner-dialog">
              <div className="banner-dialog-header">
                <h2>Confirm Delete</h2>
                <button className="banner-dialog-close" onClick={cancelDelete}>
                  <X size={20} />
                </button>
              </div>

              <div className="banner-dialog-content">
                <p>
                  Are you sure you want to delete this banner?
                  This action cannot be undone.
                </p>
              </div>

              <div className="banner-dialog-footer">
                <button
                  className="banner-cancel-btn"
                  onClick={cancelDelete}
                  disabled={deleteLoading}
                >
                  Cancel
                </button>
                <button
                  className="banner-delete-confirm-btn"
                  onClick={confirmDelete}
                  disabled={deleteLoading}
                >
                  {deleteLoading ? 'Deleting...' : 'Delete'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div >
  );
};

export default BannerProducts;
